// hooks/useFeedPageData.js - UPDATED with committee API support
import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const apiKey = process.env.REACT_APP_API_KEY
/**
 * useFeedPageData - Hook for fetching feed data with support for both standard and committee APIs
 * 
 * @param {string} schema - Database schema (e.g., 'united_states_of_america')
 * @param {string} table - Table name (e.g., 'committee_materials', 'congressional_bills')
 * @param {number} feedLength - Number of items to fetch
 * @param {string} searchQuery - Search query string
 * @param {boolean} searchPerformed - Whether a search has been initiated
 * @param {string} orderBy - Column to order by
 * @param {string} selectedAgency - Agency filter
 * @param {string} filterColumn - Filter column name
 * @param {string} filterString - Filter value
 */
export const useFeedPageData = (
  schema, 
  table, 
  feedLength, 
  searchQuery, 
  searchPerformed, 
  orderBy, 
  selectedAgency, 
  filterColumn, 
  filterString
) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();

  /**
   * Parse committee API parameters from URL
   */
  const getCommitteeParams = useCallback(() => {
    const urlParams = new URLSearchParams(location.search);
    const useCommitteeAPI = urlParams.get('useCommitteeAPI') === 'true';
    const committee = urlParams.get('committee');
    const chamber = urlParams.get('chamber');
    
    if (useCommitteeAPI && committee) {
      return { useCommitteeAPI: true, committee, chamber };
    }
    
    return { useCommitteeAPI: false };
  }, [location.search]);

  /**
   * Build the appropriate API URL based on feed type
   */
  const buildApiUrl = useCallback(() => {
    const baseUrl = 'https://api.govglance.org';
    
    // Check for committee API parameters
    const committeeParams = getCommitteeParams();
    
    // Handle committee materials with committee API
    if (committeeParams.useCommitteeAPI && committeeParams.committee) {
      let url = `${baseUrl}/committees/?limit=${feedLength}&skip=0&committee=${encodeURIComponent(committeeParams.committee)}`;
      
      if (committeeParams.chamber) {
        url += `&chamber=${encodeURIComponent(committeeParams.chamber)}`;
      }
      
      // console.log('useFeedPageData - Using committee API:', url);
      return url;
    }
    
    // Handle generic committee_materials table (all materials, no filter)
    if (table === 'committee_materials' && !committeeParams.useCommitteeAPI) {
      const url = `${baseUrl}/committees/?limit=${feedLength}&skip=0`;
      // console.log('useFeedPageData - Using generic committee API:', url);
      return url;
    }
    
    // Handle search queries
    if (searchPerformed && searchQuery.trim()) {
      return `${baseUrl}/search/${schema}/${table}?limit=${feedLength}&skip=0&search_term=${encodeURIComponent(searchQuery)}`;
    }

    // Standard feed API
    let url = `${baseUrl}/posts/recent?limit=${feedLength}&skip=0&schema=${schema}&table=${table}&order_by=${orderBy}`;
    
    // Add agency filter if present
    if (selectedAgency) {
      url += `&filter_column=agency_id&filter_string=${encodeURIComponent(selectedAgency)}`;
    } 
    // Add column filter if present
    else if (filterColumn && filterString) {
      url += `&filter_column=${encodeURIComponent(filterColumn)}&filter_string=${encodeURIComponent(filterString)}`;
    }
    
    return url;
  }, [schema, table, feedLength, searchPerformed, searchQuery, orderBy, selectedAgency, filterColumn, filterString, getCommitteeParams]);

  /**
   * Fetch data from the API
   */
  useEffect(() => {
    const fetchData = async () => {
      if (!schema || !table) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const url = buildApiUrl();
        // console.log('useFeedPageData - Fetching:', url);
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${apiKey}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        
        // Handle different response formats
        // Committee API might return data in a different structure
        if (Array.isArray(result)) {
          setData(result);
        } else if (result.data && Array.isArray(result.data)) {
          setData(result.data);
        } else if (result.items && Array.isArray(result.items)) {
          setData(result.items);
        } else {
          setData(result);
        }
        
      } catch (err) {
        setError(err.message);
        console.error('useFeedPageData - Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [schema, table, feedLength, searchPerformed, searchQuery, orderBy, selectedAgency, filterColumn, filterString, buildApiUrl]);

  return { data, loading, error };
};

export default useFeedPageData;